// ABOUTME: React Query hooks for the catalog config editor — list/get/upsert country×BL config (HCP-111556).
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import type {
  BusinessLineConfig,
  CountryConfigSummary,
  GlobalConfigurationOptions,
  UnitOfMeasureRef,
} from '../types'

const LIST_KEY = ['catalogConfig', 'list'] as const
const GLOBAL_KEY = ['catalogConfig', 'global'] as const
// Country-configurations entity lists are derived from the catalog, so they go stale on every catalog write.
const ENTITIES_KEY = ['entities'] as const
const itemKey = (countryId?: string, businessLine?: string) =>
  ['catalogConfig', countryId, businessLine] as const

export const useCountryConfigList = () =>
  useQuery<CountryConfigSummary[]>({
    queryKey: LIST_KEY,
    queryFn: async () => {
      const res = await api.get<CountryConfigSummary[]>('/tuc-configs/country-configurations')
      return res.data
    },
    staleTime: 60 * 1000,
  })

export const useCountryConfig = (countryId?: string, businessLine?: string) =>
  useQuery<BusinessLineConfig>({
    queryKey: itemKey(countryId, businessLine),
    queryFn: async () => {
      const res = await api.get<BusinessLineConfig>(
        `/tuc-configs/country-configurations/${countryId}/${businessLine}`
      )
      return res.data
    },
    enabled: Boolean(countryId && businessLine),
  })

export const useUpsertCountryConfig = (countryId?: string, businessLine?: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (config: BusinessLineConfig) => {
      const res = await api.put<BusinessLineConfig>(
        `/tuc-configs/country-configurations/${countryId}/${businessLine}`,
        config
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: itemKey(countryId, businessLine)})
      queryClient.invalidateQueries({queryKey: LIST_KEY})
      queryClient.invalidateQueries({queryKey: ENTITIES_KEY})
    },
  })
}

// Create-only: the backend returns 409 if the country×business-line already exists (never overwrites).
export const useCreateCountryConfig = (countryId?: string, businessLine?: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (config: BusinessLineConfig) => {
      const res = await api.post<BusinessLineConfig>(
        `/tuc-configs/country-configurations/${countryId}/${businessLine}`,
        config
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: LIST_KEY})
      queryClient.invalidateQueries({queryKey: ENTITIES_KEY})
    },
  })
}

export const useDeleteCountryConfig = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({countryCode, businessLine}: {countryCode: string; businessLine: string}) => {
      await api.delete(`/tuc-configs/country-configurations/${countryCode}/${businessLine}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: LIST_KEY})
      queryClient.invalidateQueries({queryKey: ENTITIES_KEY})
    },
  })
}

export const useGlobalConfig = () =>
  useQuery<GlobalConfigurationOptions>({
    queryKey: GLOBAL_KEY,
    queryFn: async () => {
      const res = await api.get<GlobalConfigurationOptions>('/tuc-configs/global-configuration')
      return res.data
    },
  })

export const useUpsertGlobalConfig = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (config: GlobalConfigurationOptions) => {
      const res = await api.put<GlobalConfigurationOptions>('/tuc-configs/global-configuration', config)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: GLOBAL_KEY})
    },
  })
}

export const useUnitsOfMeasure = () =>
  useQuery<UnitOfMeasureRef[]>({
    queryKey: ['catalogConfig', 'unitsOfMeasure'],
    queryFn: async () => {
      const res = await api.get<UnitOfMeasureRef[]>('/tuc-configs/units-of-measure')
      return res.data
    },
    staleTime: 60 * 60 * 1000,
  })
